<template>
	<div v-if="storageLimitExceeded || flags.fileDownloadsDisabled" class="cog-signature">
		<p class="cog-signature__not-available-msg">
			<em>Signature not available.</em>
			<a v-if="flags.fileDownloadsDisabled" href="" @click="upgradeStorageLimits">Upgrade</a>
		</p>
	</div>
	<div
		v-else class="cog-input cog-signature"
		:class="{
			'is-read-only': readonly,
			'cog-signature--keyboard': keyboardInput,
			'cog-signature--draw': !keyboardInput
		}"
	>
		<div ref="padContainer" class="cog-signature__pad" :class="{ 'is-loading': showSpinner }">
			<div class="cog-field__placeholder">
				<div class="cog-field__placeholder-inner">
					&nbsp;
					<i-cog />
				</div>
			</div>
			<!--
				This canvasResizePng image is to preserve the aspect ratio of the drawn version of the signature.
				During resize the contents of this image will be written to the drawn signature canvas.
			-->
			<img
				ref="canvasResizePng"
				class="canvas-png cog-offscreen"
				:src="signatureData"
				alt=""
				@load="signatureImageLoaded"
			/>
			<div
				ref="signaturePad"
				class="cog-signature__canvas-container"
				tabindex="-1"
			>
				<canvas
					ref="signaturePadCanvas"
					role="img"
					:aria-label="hasMark ? $resource('signature-present') : $resource('signature-drawing-area')"
					:aria-describedby="hasError ? `${fullId}-error` : undefined"
					:aria-invalid="hasError ? true : undefined"
				></canvas>
			</div>
			<link
				v-if="loadFont"
				:href="`https://fonts.googleapis.com/css?family=${font.replace(/ /g, '+')}&display=swap`"
				rel="stylesheet"
			>
			<canvas
				ref="textSignatureCanvas"
				class="cog-signature__text-canvas"
			></canvas>
			<input
				v-show="keyboardInput"
				:id="fullId"
				ref="keyboardInput"
				v-model="textSignature"
				:disabled="readonly || isLoading"
				:tabindex="!keyboardInput ? -1 : false"
				:style="`font-size: ${fontSize}px; font-family: '${font}', ${fontFallbacks}; color: ${readonly ? 'inherit' : signaturePadOptions.penColor};`"
				class="cog-signature__keyboard-input"
				type="text"
				spellcheck="false"
				autocomplete="off"
				:aria-required="isRequired ? true : undefined"
				:aria-describedby="hasError ? `${fullId}-error` : undefined"
				:aria-invalid="hasError ? true : undefined"
				@blur="handleBlur"
				@focus="handleFocus"
				@input="handleInput"
				@change="handleChange"
			>
			<div v-if="!readonly && !isLoading" class="cog-signature__symbol">
				<c-button
					v-if="hasMark && !keyboardInput || textSignature.length && keyboardInput"
					ref="remove"
					:title="$resource('remove-item').replace('{item}', $parent.$source ? $parent.$source.label : '')"
					class="cog-button--remove"
					no-style
					@click.native="clear"
				>
					<i-ex />
				</c-button>
				<span v-else class="cog-signature__write-symbol" aria-hidden="true">&times;</span>
			</div>
		</div>
		<div v-if="!readonly && !isLoading" class="cog-signature__toggle-buttons">
			<span ref="focusPlaceholder" class="cog-offscreen" :tabindex="focusPlaceholderActive ? 0 : -1" @blur="focusPlaceholderActive = false"></span>
			<c-button
				v-if="isAllowed('draw')"
				:aria-labelledby="id ? `cog-${id}-label` : null"
				:aria-describedby="id ? `cog-${id}-draw-description` : null"
				:aria-controls="fullId"
				:tabindex="!keyboardInput ? '-1' : false"
				no-style
				:disabled="!keyboardInput"
				@click="activateDraw"
			>
				{{ $resource('signature-draw') }}
				<span
					:id="`cog-${id}-draw-description`"
					class="cog-offscreen"
				>
					({{ $resource('switch-to-drawing-mode') }})
				</span>
			</c-button>
			<c-button
				v-if="isAllowed('type')"
				:aria-labelledby="id ? `cog-${id}-label` : null"
				:aria-describedby="id ? `cog-${id}-type-description` : null"
				:aria-controls="fullId"
				:tabindex="keyboardInput ? '-1' : false"
				no-style
				:disabled="keyboardInput"
				@click="activateKeyboard"
			>
				{{ $resource('signature-type') }}
				<span
					:id="`cog-${id}-type-description`"
					class="cog-offscreen"
				>
					({{ $resource('switch-to-typing-mode') }})
				</span>
			</c-button>
		</div>
	</div>
</template>

<script>
	import '../style/_motion.scss';
	import CButton from './Button.vue';
	import IEx from './../assets/ex-outline.svg';
	import ICog from '../assets/cog.svg';
	import debounce from '../util/debounce';
	import focus from 'src/mixins/focus';
	import { getSignatureFont, loadEmbeddableSignatureFont } from './SignatureFonts';
	import { createInvisibleInputToActivateKeyboard } from '../util/mobile-activate-keyboard';
	import SignaturePad from 'signature_pad';
	import { FormEvents } from 'src/framework/public/events';
	import resetComponentData from 'src/mixins/resetComponentData';
	import ResizeObserver from 'resize-observer-polyfill';
	import { SignatureImageError } from 'src/web-api/file-service';
	import { FormEvent } from 'src/framework/eventing/form-event';
	import { getCssVarValue } from '@cognitoforms/utils/css-helper';

	const SIGNATURE_RESIZE_THRESHOLD = 3;
	const SPINNER_VISIBILITY_DELAY = 200;
	const TRANSPARENT_PNG = {
		src: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=',
		x: 0,
		y: 0
	};

	function getScale() {
		return Math.max(window.devicePixelRatio || 1, 1) * 2;
	}

	export default {
		name: 'CSignature',
		components: { CButton, IEx, ICog },
		mixins: [focus, resetComponentData],
		inject: {
			$resource: { default: () => ({}) },
			formEvents: { default: () => ({}) },
			log: { default: () => ({}) },
			flags: { default: () => ({}) },
			fieldContext: { default: () => () => ({ hasError: false, isRequired: false }) }
		},
		props: {
			signature: {
				type: Object,
				default: () => ({
					Png: '',
					Svg: '',
					PngFile: '',
					SvgFile: '',
					TypedText: ''
				})
			},
			readonly: { type: Boolean, default: false },
			id: { type: String, default: null },
			mobile: { type: Boolean, default: false },
			fileService: { type: Object, required: true },
			allowedInput: {
				type: String,
				default: 'drawandtype',
				validator: value => {
					return ['draw', 'type', 'drawandtype'].includes(value);
				}
			}
		},
		data() {
			return {
				// used to prevent premature interaction with the signature pad while the image loads
				isLoading: this.isLoading === undefined ? true : this.isLoading,
				showSpinner: false,
				signaturePad: {},
				signatureData: TRANSPARENT_PNG.src,
				hasMark: null,
				keyboardInput: false,
				loadFont: false,
				textSignature: '',
				isFocus: false,
				fontSize: 70,
				width: null,
				font: 'Mrs Saint Delafield',
				fontFallbacks: '\'Segoe Script\', \'Bradley Hand ITC\', \'Brush Script MT\', cursive',
				embeddableFontName: null,
				embeddableFontCss: null,
				embeddableFontCssPromise: null,
				debouncedHandleResize: debounce(() => this.handleResize()),
				signaturePadOptions: {
					penColor: getCssVarValue('--text-primary') || 'black',
					onEnd: this.onEnd
				},
				toggleCache: {
					Png: null,
					Svg: null,
					TypedText: null
				},
				focusPlaceholderActive: false,
				canvasDimensions: null,
				resizeObserver: new ResizeObserver(() => this.debouncedHandleResize()),
				storageLimitExceeded: false
			};
		},
		computed: {
			penIsBlack() {
				return this.signaturePadOptions.penColor === 'black';
			},
			hasError() {
				return this.fieldContext().hasError;
			},
			fullId() {
				return this.id ? 'cog-' + this.id : null;
			},
			isRequired() {
				return this.fieldContext().isRequired;
			}
		},
		watch: {
			readonly() {
				if (this.readonly) {
					this.updateSignatureColor();
					this.disableSignature();
				}
				else {
					this.updateSignatureColor();
					this.enableSignature();
				}
			},
			isLoading() {
				if (this.isLoading) {
					this.disableSignature();
					// don't show the spinner if the image loads quickly
					setTimeout(() => (this.showSpinner = this.isLoading), SPINNER_VISIBILITY_DELAY);
				}
				else if (!this.readonly) {
					this.showSpinner = false;
					this.enableSignature();
				}
			},
			signaturePad(newSignaturePad, oldSignaturePad) {
				if (oldSignaturePad && oldSignaturePad.off)
					oldSignaturePad.off();
			}
		},
		beforeUpdate() {
			this.disableSignature();
		},
		async updated() {
			if (!this.signaturePad.off)
				await this.initiateCanvas();

			if (this.readonly)
				this.disableSignature();
			else if (!this.isLoading)
				this.enableSignature();

			if (!this.isAllowed('draw')) {
				this.loadFont = true;
				this.keyboardInput = true;
			}
		},
		created () {
			// Setup resetComponentData mixin
			this.valueName = ['signature.Png', 'signature.PngFile'];
			this.changeHandlerName = ['clear', 'captureTypedSignature', 'captureSignatureFromCanvas'];
			this.addResetDataCallback(this.initiateCanvas);
		},
		async mounted() {
			if (!this.flags.fileDownloadsDisabled)
				this.changePenColor(false, this.calculatePenColor());
			await this.initiateCanvas();
			this.resizeObserver.observe(this.$el);
		},
		beforeDestroy() {
			this.resizeObserver.unobserve(this.$el);
		},
		methods: {
			enableSignature() {
				this.signaturePad.on();
			},
			disableSignature() {
				this.signaturePad.off();
			},
			updateSignatureColor() {
				if (!this.canvasDimensions)
					return;

				const canvas = this.$refs.signaturePadCanvas.getContext('2d');
				canvas.save();
				canvas.fillStyle = this.calculatePenColor();
				canvas.globalCompositeOperation = 'source-in';
				canvas.fillRect(0, 0, this.canvasDimensions.width, this.canvasDimensions.height);
				canvas.restore();
			},
			signatureImageLoaded() {
				this.isLoading = false;
			},
			async initiateCanvas() {
				if (this.flags.fileDownloadsDisabled)
					return;

				this.storageLimitExceeded = false;

				this.formEvents?.on(FormEvents.ThemeUpdated, () => {
					const newColor = this.calculatePenColor();
					if (this.signaturePad.penColor !== newColor) {
						// Clear drawn signature but not typed
						this.signaturePad.clear();
						this.hasMark = false;
						this.changePenColor(true, newColor);
					}
				});

				const canvas = this.$refs.signaturePadCanvas;
				this.signaturePad = new SignaturePad(canvas, this.signaturePadOptions);
				// Lock signature to start. May become unlocked after image loads if not readonly
				this.disableSignature();
				this.signaturePad.addEventListener('endStroke', this.onEnd);

				let png = this.signature.Png;
				if (this.signature.PngFile) {
					try {
						png = await this.fileService.getSignatureImageData(this.signature, 'png');

						// If the signature cannot be fetched (for whatever reason), clear the signature.
						if (!png) {
							this.clear();
						}
					}
					catch (err) {
						if (err instanceof SignatureImageError && err.storageLimitExceeded) {
							this.storageLimitExceeded = true;
						}
						// do not load signature so users don't think it's not filled out
						this.isLoading = true;
						return;
					}
				}

				this.hasMark = !!png;

				// Activate input if text signature is saved and clear from the canvas (saves to canvas as default)
				if (this.signature.TypedText && this.signature.TypedText.trim() !== '') {
					this.textSignature = this.signature.TypedText;
					this.loadFont = true;
					this.keyboardInput = true;
				}

				if (this.hasMark && !this.keyboardInput) {
					// Convert stored black signature to display version with current pen color
					const displayPng = this.penIsBlack ? png : await this.createDisplayVersion(png);

					const isNewImg = this.$refs.canvasResizePng.src !== displayPng;
					if (isNewImg) {
						this.isLoading = true;
						this.$refs.canvasResizePng.onload = () => {
							this.$refs.canvasResizePng.onload = null;
							this.drawImageToCanvas();
						};
					}
					else
						this.handleResize();

					this.signatureData = displayPng;
				}
				else {
					this.isLoading = false;
					this.handleResize();
				}
			},
			calculatePenColor() {
				// Get the color of input text and set ink color to that value
				const el = document.createElement(this.readonly ? 'div' : 'input');
				const elContainer = document.createElement('div');
				elContainer.setAttribute('aria-hidden', 'true');
				if (!this.readonly)
					el.classList.add('el-input__inner');
				el.classList.add('cog-offscreen');
				elContainer.classList.add('cog-signature', 'cog-signature__pad');
				elContainer.appendChild(el);
				this.$root.$refs.body.appendChild(elContainer);

				const color = getComputedStyle(el).color;
				elContainer.parentNode.removeChild(elContainer);

				return color;
			},
			changePenColor(runtimeChange, inputColor) {
				this.signaturePadOptions.penColor = inputColor;
				if (runtimeChange)
					this.signaturePad.penColor = inputColor;
			},
			handleResize() {
				const resizeHeight = this.width && Math.abs(this.width - this.$el.querySelector('canvas').clientWidth) <= SIGNATURE_RESIZE_THRESHOLD;

				if (resizeHeight)
					return false;

				this.resizeSigCanvas();
				this.resizeTextCanvas();
			},
			captureData() {
				if ((this.keyboardInput && !this.signature.TypedText) || (!this.keyboardInput && this.signaturePad.isEmpty())) {
					this.toggleCache = {
						Png: null,
						Svg: null,
						TypedText: null
					};
					return;
				}

				if (this.keyboardInput)
					this.captureTypedSignature();
				else
					this.captureSignatureFromCanvas();

				this.toggleCache = {
					Png: this.signature.Png,
					Svg: this.signature.Svg,
					TypedText: this.signature.TypedText
				};
			},
			resizeCanvas(canvas) {
				if (!canvas)
					return;

				if (!this.$el.closest('.cog-form').getAttribute('data-width')) {
					requestAnimationFrame(() => {
						this.resizeCanvas(canvas);
					});
					return;
				}

				requestAnimationFrame(() => {
					const ctx = canvas.getContext('2d');
					const minHeight = 100;
					const maxHeight = 250;
					// Increase the scale based on pixel density as well upsizing to prevent pixilazation
					// when going from small signature to large signature.
					const scale = getScale();
					this.width = Math.round(canvas.offsetWidth);

					canvas.width = this.width * scale;

					// Preserve the aspect ratio of drawn signatures at the expense of honoring minHeight.
					if (this.hasMark && !this.keyboardInput)
						canvas.height = Math.min(canvas.width * .5, maxHeight * scale);
					else
						canvas.height = Math.min(Math.max(canvas.width * .5, minHeight * scale), maxHeight * scale);

					this.canvasDimensions = {
						width: canvas.width * scale,
						height: canvas.height * scale
					};
					ctx.scale(scale, scale);

					// Need to resize sigcanvas regardless of the mode.
					if (this.hasMark) {
						this.drawImageToCanvas();
					}

					this.resizingMarks = false;
				});
			},
			drawImageToCanvas() {
				try {
					const sigCanvas = this.$refs.signaturePadCanvas;
					const sigCtx = sigCanvas.getContext('2d');
					const scale = getScale();
					sigCtx.drawImage(this.$refs.canvasResizePng, 0, 0, sigCanvas.width / scale, sigCanvas.height / scale);
				}
				catch (ex) {
					// sigCtx.drawImage(...) is known to throw an exception when the signature pad gets into an invalid state.
					this.fileService.reportSignatureCaptureError(ex);
					this.clear();
				}
			},
			resizeSigCanvas() {
				const canvas = this.$refs.signaturePadCanvas;
				this.resizeCanvas(canvas);
			},
			resizeTextCanvas() {
				const canvas = this.$refs.textSignatureCanvas;
				this.resizeCanvas(canvas);

				setTimeout(()=>{
					this.addTextToCanvas();
				}, 200);
			},
			detectWritingSystem() {
				const input = this.$refs.keyboardInput;
				const text = input.value;
				let writingSystem = 'latin';

				const punctuationlessText = text.replace(/[ .,/#!$%^&*;:{}=\-_`~()"?\d]/g, '');
				const charCodes = [...punctuationlessText].map(char => {return char.charCodeAt(0);});

				const isLatin = code => code < 913;
				const isGreek = code => code >= 913 && code <= 969;
				const isCyrillic = code => code >= 1040 && code <= 1103;
				const isArabic = code => code >= 1548 && code <= 1617;
				const isDevanagari = code => code >= 2306 && code <= 2404;
				const isThai = code => code >= 3585 && code <= 3660;
				const isKorean = code => code >= 43360 && code <= 55291;
				const isChinese = code => code >= 12290 && code <= 65292;

				if (charCodes.every(isLatin))
					writingSystem = 'latin';
				else if (charCodes.every(isGreek))
					writingSystem = 'greek';
				else if (charCodes.every(isCyrillic))
					writingSystem = 'cyrillic';
				else if (charCodes.every(isArabic))
					writingSystem = 'arabic';
				else if (charCodes.every(isDevanagari))
					writingSystem = 'devanagari';
				else if (charCodes.every(isThai))
					writingSystem = 'thai';
				else if (charCodes.every(isKorean))
					writingSystem = 'korean';
				else if (charCodes.every(isChinese))
					writingSystem = 'chinese';

				// Get a cursive Google font to use
				this.font = getSignatureFont(writingSystem);
			},
			ensureEmbeddableFont(name) {
				// Only load the font if it matches the current font name and hasn't already been loaded
				if (this.font === name && !(this.embeddableFontCss && this.embeddableFontName === name)) {
					if (this.embeddableFontCssPromise && this.embeddableFontName !== name) {
						// Discard the promise if the font name is different
						this.embeddableFontName = null;
						this.embeddableFontCssPromise = null;
					}
					// Load the embeddable font...
					if (!this.embeddableFontCssPromise) {
						this.embeddableFontName = name;
						this.embeddableFontPromise = loadEmbeddableSignatureFont(name);
						this.embeddableFontPromise.then(f => {
							// Discard the results if the font has changed
							if (this.font === name) {
								this.embeddableFontCss = f.replace(/[\r\n]/g, '');
								this.embeddableFontPromise = null;
							}
						});
					}
				}
			},
			addTextToCanvas() {
				if (!this.$refs.textSignatureCanvas)
					return;
				const canvas = this.$refs.textSignatureCanvas;
				const ctx = canvas.getContext('2d');
				const signatureXposition = 40;

				ctx.font = `${this.fontSize}px '${this.font}', ${this.fontFallbacks}`;
				ctx.fillStyle = this.signaturePadOptions.penColor;

				// If text is too long, shrink
				while (this.fontSize > 10 && ctx.measureText(this.textSignature).width > this.width - signatureXposition - 10) {
					ctx.font = `${this.fontSize}px '${this.font}', ${this.fontFallbacks}`;
					this.fontSize = this.fontSize - 1;
				}

				// If text is less than 70px and there is room, enlarge
				while (this.fontSize <= 70 && ctx.measureText(this.textSignature).width < this.width - signatureXposition - 50) {
					ctx.font = `${this.fontSize}px '${this.font}', ${this.fontFallbacks}`;
					this.fontSize = this.fontSize + 1;
				}

				this.clearCanvas(canvas);
				// might need to add some to last argument for super small fontSizes.
				ctx.fillText(this.textSignature, signatureXposition, canvas.offsetHeight * .7);
			},
			clearCanvas(canvas) {
				canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
			},
			getTextSignatureSvg() {
				return `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 ${this.width} ${Math.round(this.width / 2)}">${this.getTextSignatureFontStyle() || ''}<title>${this.textSignature}</title><text x="30" y="${Math.round(this.width / 3)}" style="fill: ${this.signaturePadOptions.penColor}; font: ${this.fontSize}px '${this.font}', ${this.fontFallbacks};">${this.textSignature}</text></svg>`;
			},
			getTextSignatureFontStyle() {
				if (this.font && this.embeddableFontCss && this.embeddableFontName === this.font) {
					return `<style>${this.embeddableFontCss}</style>`;
				}
			},
			handleInput() {
				this.addTextToCanvas();
				this.detectWritingSystem();
				if (this.font)
					this.ensureEmbeddableFont(this.font);
			},
			handleChange() {
				this.captureTypedSignature();
			},
			captureTypedSignature(data) {
				if (!this.$refs.keyboardInput || !this.$refs.keyboardInput.value || this.$refs.keyboardInput.value.trim() === '') {
					this.clear();
					return;
				}

				const { Png, Svg, TypedText } = data || {};

				// Create temporary invisible canvas for black PNG storage
				const blackPng = this.penIsBlack ? this.$refs.textSignatureCanvas.toDataURL('image/png') : this.createStoredTextVersion();

				this.signature.Png = Png || blackPng;
				this.signature.Svg = Svg || 'data:image/svg+xml;utf8,' + escape(this.getTextSignatureSvg());
				this.signature.TypedText = TypedText || this.textSignature;
			},
			clear() {
				if (this.keyboardInput) {
					this.textSignature = '';
					this.keyboardInput = true;
					this.clearCanvas(this.$refs.textSignatureCanvas);
					setTimeout(()=> {
						this.$refs.keyboardInput.focus();
					});
				}
				else {
					this.signaturePad.clear();
					this.hasMark = false;
					this.saveFocus();
				}

				this.signature.Png = null;
				this.signature.PngFile = null;
				this.signature.Svg = null;
				this.signature.SvgFile = null;
				this.signature.TypedText = null;
			},
			onEnd() {
				this.hasMark = true;
				this.saveFocus('remove');
				this.captureSignatureFromCanvas();
			},
			captureSignatureFromCanvas({ Png, Svg } = {}) {
				// Create storage version (black ink for storage)
				const storagePng = this.penIsBlack ? Png || this.signaturePad.toDataURL('image/png') : this.applyColorToCanvas(this.$refs.signaturePadCanvas);
				this.signature.Png = storagePng;

				// Check if the signature failed to capture, if so try again. If that fails, clear the signature.
				if (!this.didImageCapture()) {
					this.signature.Png = this.penIsBlack ? this.signaturePad.toDataURL('image/png') : this.applyColorToCanvas(this.$refs.signaturePadCanvas);
					if (!this.didImageCapture()) {
						this.clear();
						this.fileService.reportSignatureCaptureError();
						return;
					}
				}

				if (this.hasMark)
					this.signatureData = Png || this.signature.Png;
				this.signature.Svg = Svg || this.signaturePad.toDataURL('image/svg+xml');
				this.signature.TypedText = null;
			},
			activateDraw() {
				const tempCache = this.toggleCache;
				this.captureData();
				this.keyboardInput = false;
				if (this.hasMark) {
					this.saveFocus('remove');
					this.captureSignatureFromCanvas(tempCache);
				}
				else {
					this.saveFocus();
					this.signature.Png = null;
					this.signature.Svg = null;
					this.signature.TypedText = null;
					this.signature.PngFile = null;
					this.signature.SvgFile = null;
				}
			},
			activateKeyboard() {
				const tempCache = this.toggleCache;
				this.captureData();
				this.handleFocus();
				this.loadFont = true;
				this.captureTypedSignature(tempCache);

				// For mobile, focus an invisible input immediately so that the device will show the keyboard to the user
				if (this.mobile) {
					const invisibleInputContainer = this.$refs.keyboardInput.parentElement;
					const invisibleInputForMobileKeyboard = createInvisibleInputToActivateKeyboard(invisibleInputContainer);
					setTimeout(()=> {
						this.$refs.keyboardInput.focus();
						// Remove the input used to show the keyboard on mobile devices
						invisibleInputForMobileKeyboard.remove();
					});
				}
				else {
					setTimeout(()=> {
						this.$refs.keyboardInput.focus();
					});
				}
			},
			handleBlur() {
				this.removeClassOnBlur();
			},
			handleFocus() {
				this.keyboardInput = true;
				this.addClassOnFocus();
			},
			saveFocus(el = 'focusPlaceholder') {
				this.$nextTick(()=> {
					if (el === 'focusPlaceholder') {
						this.focusPlaceholderActive = true;
						this.$refs.focusPlaceholder.focus();
					}
					else if (el === 'remove')
						this.$refs.remove.$el.focus();
				});
			},
			isAllowed(type) {
				return this.allowedInput ? this.allowedInput.includes(type) : false;
			},
			upgradeStorageLimits(evt) {
				evt.preventDefault();
				this.formEvents?.emit(new FormEvent(FormEvents.TryDownloadOverLimit));
			},
			applyColorToCanvas(sourceCanvas, img = null, color = 'black') {
				// Create a temporary canvas to render with the specified color
				const tempCanvas = document.createElement('canvas');
				const tempCtx = tempCanvas.getContext('2d');

				tempCanvas.width = sourceCanvas.width;
				tempCanvas.height = sourceCanvas.height;

				tempCtx.drawImage(img ?? sourceCanvas, 0, 0);

				// Apply color using composite operation
				tempCtx.globalCompositeOperation = 'source-in';
				tempCtx.fillStyle = color;
				tempCtx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);

				return tempCanvas.toDataURL('image/png');
			},
			createDisplayVersion(blackPngDataUrl) {
				if (!blackPngDataUrl)
					return null;

				return new Promise((resolve) => {
					const img = new Image();
					img.onload = () => {
						const tempCanvas = document.createElement('canvas');
						tempCanvas.width = img.width;
						tempCanvas.height = img.height;

						resolve(this.applyColorToCanvas(tempCanvas, img, this.signaturePadOptions.penColor));
					};
					img.src = blackPngDataUrl;
				});
			},
			didImageCapture() {
				const malformedImages = ['data:image/png;base64', 'data:image/png;base64,', 'data:', 'data:,'];
				return !(malformedImages.includes(this.signature.Png));
			},
			createStoredTextVersion() {
				const tempCanvas = document.createElement('canvas');
				const tempCtx = tempCanvas.getContext('2d');

				const displayCanvas = this.$refs.textSignatureCanvas;

				tempCanvas.width = displayCanvas.width;
				tempCanvas.height = displayCanvas.height;

				// Apply the same scaling
				const scale = getScale();
				tempCtx.scale(scale, scale);

				// Render text in black
				const signatureXposition = 40;
				tempCtx.font = `${this.fontSize}px '${this.font}', ${this.fontFallbacks}`;
				tempCtx.fillStyle = 'black';
				tempCtx.fillText(this.textSignature, signatureXposition, displayCanvas.offsetHeight * .7);

				return tempCanvas.toDataURL('image/png');
			}
		}
	};
</script>

<style lang="scss">
#{$specificity-base} {

	.cog-signature {
		min-width: 100px;

		&.cog-col--7,
		&.cog-col--8,
		&.cog-col--9 {
			min-width: 180px;
		}

		// Will apply to columns 10-24 since 4 columns is the narrowest a signature can get.
		&[class*='cog-col--1'],
		&[class*='cog-col--2'] {
			min-width: 220px;
		}

		canvas {
			display: block;
			width: 100%;
			height: 100%;
		}
	}

	.cog-signature__canvas-container {
		position: relative;
		z-index: $signature--z;
		transition: max-height .3s;
	}

	// 🔝 = Even though this is set to position: absolute, this canvas seems
	// to subtly affect the position of other elements without this in FF.
	.cog-signature--keyboard .cog-signature__canvas-container canvas,
	.cog-signature--draw .cog-signature__text-canvas {
		position: absolute;
		top: 0; // 🔝
		visibility: hidden;
	}

	.cog-signature__text-canvas {
		visibility: hidden;
	}

	.cog-signature__toggle-buttons {
		text-align: right;
	}

	.cog-signature__toggle-buttons button {
		margin-top: .3em;
		margin-left: .2em;
		padding: .2em .5em;
		font-size: var(--small-text);

		@include styled {

			.cog-signature__toggle-buttons button:focus {
				outline: 1px solid var(--highlight);
			}
		}
	}

	.cog-cognito--styled .cog-signature__toggle-buttons [disabled] {
		border-bottom: calc(var(--border-width) + 1px) solid var(--highlight);
		cursor: default !important;
		opacity: 1;
	}

	.cog-signature__pad {
		position: relative;
		background: var(--input__background-color);

		// border-right because if there is no border on the right there should not be a border at all. E.g. Underline theme
		border: var(--input__border-color) var(--input__border-width-right, var(--input__border-width)) var(--input__border-style);
		border-radius: var(--border-radius);
		box-shadow: var(--input__box-shadow);
		overflow: hidden;
		transition: max-height $speed-quarter;

		> div:not(.cog-field__placeholder) {
			height: auto !important;
		}

		.cog-field__placeholder {
			position: absolute;
			top: -8%;
			width: 100%;
			height: 0;
			overflow: hidden;
			opacity: 0;
		}

		&.is-loading .cog-field__placeholder {
			height: auto;
			opacity: 1;
		}

		&:not(.is-loading)::after {
			display: block;
			position: absolute;
			bottom: 20%;
			left: #{$gutter-half};
			z-index: 0;
			width: calc(100% - var(--gutter));
			height: 0;
			border-bottom: var(--signature__line-color, hsla(var(--background-hsl), .6)) var(--input__border-width-bottom, var(--input__border-width)) var(--input__border-style);
			content: '';
			pointer-events: none;
		}

		@include at-root {
			// For Underline theme
			.is-focus .cog-signature__pad::after {
				border-bottom-color: var(--signature__line-color-focus, var(--signature__line-color, hsla(var(--background-hsl), .6)));
			}
		}

		@include styled {

			.is-focus .cog-signature__pad {
				border-color: var(--highlight);
			}
		}

		@include at-root {

			.cog-input.is-read-only .cog-signature__pad {
				background: none;
				border-color: transparent;
			}

			.cog-signature--draw:not(.is-read-only) .cog-signature__pad:not(.is-loading) {
				cursor: crosshair;
				// stylelint-disable-next-line declaration-block-no-duplicate-properties
				cursor: image-set(url('../assets/graphics/pen.png') 1x, url('../assets/graphics/pen@2x.png') 2x) 0 16, crosshair;
			}

			.is-read-only.cog-signature--draw .cog-signature__pad {
				background: none;
			}
		}
	}

	.cog-signature__symbol {
		position: absolute;
		bottom: 25%;
		left: $gutter-half;

		// Only if remove button
		z-index: $signature--z;
		color: var(--input__color, inherit);

		.cog-button--remove {
			position: relative;
			bottom: -4px;
			left: -5px;
			color: inherit;
			font-size: var(--font-size, inherit);

			&:focus .cog-icon * {
				fill: var(--input-highlight-reverse);
				stroke: var(--input-highlight);
			}
		}
	}

	.is-read-only .cog-signature__symbol {
		display: none;
	}

	.cog-signature__write-symbol {
		display: block;
		font-size: 1.5em;
		line-height: .6;
	}

	.cog-signature__keyboard-input {
		position: absolute;
		bottom: 0;
		left: 0;
		top: 0;
		z-index: $signature--z;
		width: 100%;
		height: 100%;
		background: transparent;
		border: 0;
		color: var(--input__color, inherit);
		text-indent: 40px;

		@include at-root(2) {

			.cog-signature__keyboard-input {
				padding: min(21%, 120px) 0 0;
				font-weight: normal;
				line-height: 1;
			}
		}
	}
}
</style>